import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Shield, Phone, CheckCircle, ArrowRight, Calendar, Clock, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { FAQSection } from '../components/FAQSection';
import { TRANSPORT_FAQS } from '../lib/faqData';

const LOGO_URL = "/pt-logo.png?v=2";
const WHATSAPP_LINK = "https://wa.me/message/RYIKLUKDZT2NC1";

const SERVICE_OPTIONS = [
  {
    id: 'lakewood_to_orlando',
    title: 'Lakewood to Orlando',
    description: 'Transport your vehicle from Lakewood, NJ to Orlando, FL before Pesach',
    price: 1500,
    direction: 'south'
  },
  {
    id: 'round_trip',
    title: 'Round Trip',
    description: 'Complete round trip transport - to Orlando before Pesach and back after',
    price: 2800,
    direction: 'both',
    popular: true
  },
  {
    id: 'orlando_to_lakewood',
    title: 'Orlando to Lakewood',
    description: 'Transport your vehicle from Orlando, FL back to Lakewood, NJ after Pesach',
    price: 1500,
    direction: 'north'
  }
];

const FEATURES = [
  {
    icon: Shield,
    title: 'Fully Insured',
    description: 'Your vehicle is protected throughout the entire journey with comprehensive coverage'
  },
  {
    icon: Clock,
    title: 'Reliable Timing',
    description: 'We ensure your car arrives before Pesach and returns promptly after'
  },
  {
    icon: MapPin,
    title: 'Door-to-Door',
    description: 'Convenient pickup and delivery directly at your specified addresses'
  }
];

export default function HomePage() {
  const navigate = useNavigate();
  const [adminClickCount, setAdminClickCount] = useState(0);

  const handleSelectService = (serviceId) => {
    navigate(`/booking?service=${serviceId}`);
  };

  const handleFooterClick = () => {
    const newCount = adminClickCount + 1;
    setAdminClickCount(newCount);
    if (newCount >= 3) {
      navigate('/admin');
      setAdminClickCount(0);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src={LOGO_URL} alt="Platinum Touch" className="h-14 w-auto" />
              <span className="font-bold text-xl text-slate-900 font-['Outfit']">Platinum Touch</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/faq" className="text-sm font-medium text-slate-600 hover:text-[#38B6FF] transition-colors hidden sm:inline" data-testid="header-faq-link">FAQ</Link>
              <Button
              onClick={() => navigate('/booking')}
              className="bg-[#38B6FF] hover:bg-blue-500 text-white font-semibold px-5 py-2 rounded-lg shadow-sm transition-all duration-200 active:scale-95"
              data-testid="book-now-header-btn"
            >
              Book Now
            </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden pt-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/hero-transport.jpg')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/60" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full mb-6 animate-fade-in">
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Pesach 2026 Bookings Now Open</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6 font-['Outfit'] animate-fade-in stagger-1">
              Lakewood to Orlando<br />
              <span className="text-[#38B6FF]">Car Transport Service</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed animate-fade-in stagger-2">
              Professional, reliable vehicle transport for the Pesach season. 
              Your car delivered safely, on time, every time.
            </p>
            
            <div className="flex justify-center animate-fade-in stagger-3">
              <Button
                onClick={() => navigate('/booking')}
                className="bg-[#38B6FF] hover:bg-blue-400 text-white font-semibold px-10 py-4 rounded-lg shadow-lg transition-all duration-200 active:scale-95 text-lg"
                data-testid="hero-book-now-btn"
              >
                Book Your Transport
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/80 rounded-full" />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white" id="services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-['Outfit'] tracking-tight">
              Choose Your Service
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Select the transport option that best fits your needs for the Pesach season
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {SERVICE_OPTIONS.map((service, index) => (
              <div
                key={service.id}
                className={`relative bg-white border rounded-2xl p-6 lg:p-8 transition-all duration-300 cursor-pointer group hover:shadow-xl hover:border-[#38B6FF] ${
                  service.popular ? 'border-[#38B6FF] shadow-lg' : 'border-slate-200'
                }`}
                onClick={() => handleSelectService(service.id)}
                data-testid={`service-card-${service.id}`}
              >
                {service.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#38B6FF] text-white text-xs font-semibold px-4 py-1 rounded-full uppercase tracking-wide">
                      Best Value
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    service.popular ? 'bg-[#38B6FF]' : 'bg-slate-100 group-hover:bg-[#38B6FF]'
                  } transition-colors`}>
                    {service.direction === 'south' && (
                      <ArrowRight className={`w-6 h-6 rotate-90 ${service.popular ? 'text-white' : 'text-slate-600 group-hover:text-white'} transition-colors`} />
                    )}
                    {service.direction === 'north' && (
                      <ArrowRight className={`w-6 h-6 -rotate-90 ${service.popular ? 'text-white' : 'text-slate-600 group-hover:text-white'} transition-colors`} />
                    )}
                    {service.direction === 'both' && (
                      <div className="flex flex-col">
                        <ArrowRight className="w-4 h-4 rotate-90 text-white" />
                        <ArrowRight className="w-4 h-4 -rotate-90 text-white -mt-1" />
                      </div>
                    )}
                  </div>
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-2 font-['Outfit']">
                  {service.title}
                </h3>
                <p className="text-slate-600 mb-6 text-sm leading-relaxed">
                  {service.description}
                </p>

                <div className="flex items-end justify-between">
                  <div>
                    <span className="text-3xl font-bold text-slate-900 font-['Outfit']">
                      ${service.price.toLocaleString()}
                    </span>
                  </div>
                  <Button 
                    className={`${
                      service.popular 
                        ? 'bg-[#38B6FF] hover:bg-blue-500 text-white' 
                        : 'bg-slate-100 hover:bg-[#38B6FF] text-slate-700 hover:text-white'
                    } font-medium px-5 py-2 rounded-lg transition-all duration-200`}
                    data-testid={`select-service-btn-${service.id}`}
                  >
                    Select
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 font-['Outfit'] tracking-tight">
                Why Choose Our Service?
              </h2>
              <p className="text-lg text-slate-600 mb-10 leading-relaxed">
                We specialize in seasonal vehicle transport between Lakewood, NJ and Orlando, FL. 
                Our experienced team ensures your vehicle arrives safely and on time.
              </p>
              
              <div className="space-y-6">
                {FEATURES.map((feature, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-[#38B6FF]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1 font-['Outfit']">{feature.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_auto-courier-reserve/artifacts/74fmc201_IMG_0497.jpeg"
                  alt="Honda Odyssey minivan"
                  className="w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 font-['Outfit']">100%</p>
                  <p className="text-sm text-slate-600">Fully Insured</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        faqs={TRANSPORT_FAQS}
        accentColor="#38B6FF"
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about our auto transport service"
        sectionId="transport-faq"
      />

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit'] tracking-tight">
            Ready to Book Your Transport?
          </h2>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            Secure your spot for Pesach 2026. Spaces fill up quickly, book early to guarantee your preferred dates.
          </p>
          <Button
            onClick={() => navigate('/booking')}
            className="bg-[#38B6FF] hover:bg-blue-400 text-white font-semibold px-10 py-4 rounded-lg shadow-lg transition-all duration-200 active:scale-95 text-lg"
            data-testid="cta-book-now-btn"
          >
            Start Booking Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Platinum Touch" className="h-12 w-auto" />
              <span className="font-semibold text-lg text-white font-['Outfit']">Platinum Touch Auto Transport</span>
            </div>
            
            {/* Phone Number */}
            <a
              href="tel:682-253-2633"
              className="inline-flex items-center gap-2 text-[#38B6FF] hover:text-blue-400 transition-colors"
              data-testid="footer-phone"
            >
              <Phone className="w-5 h-5" />
              <span className="font-medium">682-253-2633</span>
            </a>
            
            {/* Hidden Admin Access - Click 3 times to access */}
            <p 
              className="text-slate-500 text-sm cursor-pointer select-none"
              onClick={handleFooterClick}
              data-testid="footer-copyright"
            >
              © 2026 Platinum Touch. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 flex items-center gap-2 group"
        data-testid="whatsapp-button"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap font-medium text-sm">
          Chat with us
        </span>
      </a>
    </div>
  );
}
